import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {ReactNode, useMemo, useState} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {UpdateProcedureRunParams} from 'procedures/network/ProcedureRunDAO';
import {
  ExperimentalInputs,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import useTimingEstimate from 'studies/progress/useTimingEstimate';
import {TimingEstimateResult} from 'studies/progress/useTimingEstimate';
import {
  Condition,
  useConditionsAndExperimentalInputs,
  useExperimentalInputsContextProperties,
  useProcedureRunUpdateParam,
} from 'studies/typed/process-development/useProcessDevelopmentStudyState';
import {ReplicateTable} from 'studies/typed/shared/ReplicateTable';
import {
  getProcedureRunUIStringForStudyType,
  getProcedureTemplateStringForStudyType,
} from 'studies/TypedStudyUtils';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

import MultiBatchButton from '../process-development/multi-batch-selector/MultiBatchButton';
import {MULTI_BATCH_BUTTON_CONFIG_BY_DATA_ENTRY_MODE} from '../process-development/multi-batch-selector/MultiBatchSelector';
import {getTypedStudyAdapter} from '../TypedStudyAdapters';
import {StudyInaccessibleContentBanner} from '../utils/ui/StudyQueryBanners';
import {
  MultiProcedureRunStudyDesignTabContentsQuery,
  MultiProcedureRunStudyDesignTabContentsQueryVariables,
  MultiProcedureRunStudyDesignTabContentsQuery_procedureRun_ProcedureRunNode_templateProcedureFlowchartVersion_ProcedureFlowchartVersionNode as AccessibleTemplateProcedureFlowchartVersionNode,
  MultiProcedureRunStudyDesignTabContentsQuery_procedureRun_ProcedureRunNode as ProcedureRunNode,
  MultiProcedureRunStudyDesignTabContentsQuery_procedureRun_ProcedureRunNode_procedureFlowchartConfigVersionProxy_ProcedureFlowchartConfigVersionProxyNode as AccessibleProcedureFlowchartConfigVersionProxyNode,
} from './__generated/MultiProcedureRunStudyDesignTabContentsQuery';
import {
  getConditionCreationUnknownErrorAction,
  onOpenAfterAddConditions,
  onSuccessAfterAddConditions,
} from './ConditionCreationTrayTask';
import {EditNumConditionsButtons} from './EditNumConditionsButtons';
import {ExperimentFlowchart, ExperimentMethod} from './ExperimentalInputs';
import getStudyDesignFromProcedureRun, {
  STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT,
} from './getStudyDesignFromProcedureRun';
import MethodExecutionLimitError from './MethodExecutionLimitError';
import {ProcedureRunTab} from './MultiProcedureRunOverviewTabContents';
import {onAddConditions} from './onAddConditions';
import ProcedureRunPlan, {ExperimentalInputsContext} from './ProcedureRunPlan';
import {
  areProcedureRunInputsEvaluatedValuesValid,
  isNumConditionReplicatesValid,
  validateConditions,
} from './procedureRunStudyValidationUtils';
import StudyDesignTabSavingButtons, {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {MethodExecutionLimitState, useMethodExecutionLimitState} from './useMethodExecutionLimitState';

const PARAMETER_VALUES_TAB_NAME = 'Conditions';
const SETTINGS_TAB_NAME = 'Settings';

export const MULTI_PROCEDURE_RUN_STUDY_DESIGN_TAB_CONTENTS_QUERY: DocumentNode = gql`
  query MultiProcedureRunStudyDesignTabContentsQuery($procedureRunId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedureRun(id: $procedureRunId) {
      ...GetStudyDesignFromProcedureRunGraphQL
      ... on ProcedureRunNode {
        id
        procedureType
        defaultDataEntryMode
        flowchartIsExpanded
        conditions {
          id
          name
          description
          procedureRunConditionReplicates {
            id
          }
        }
        templateProcedureFlowchartVersion {
          ...ExperimentalInputsFlowchartGraphQL
          ... on ProcedureFlowchartVersionNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedureFlowchart {
              id
              parent {
                id
              }
            }
          }
        }
        procedureFlowchartConfigVersionProxy {
          ... on ProcedureFlowchartConfigVersionProxyNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcedureFlowchartVersion').fragment}
  ${STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT}
`;

/**
 * Returns an array of indices indicating the positions of each initial condition
 * within the full list of conditions, based on matching condition IDs.
 * This is necessary to handle the case where the user reorders the persisted conditions.
 *
 * @param initialConditions - An array of initial `Condition` objects to locate within `conditions`.
 * @param conditions - The full array of `Condition` objects to search.
 * @returns An array of indices corresponding to the positions of each initial condition in `conditions`.
 */
function getInitialConditionIndices(
  initialConditions: Array<Condition>,
  conditions: Array<Condition>
): Array<number> {
  return initialConditions.map((initialCondition) =>
    conditions.findIndex((c) => c.id === initialCondition.id)
  );
}

function ParameterValuesTabContents(props: {
  methods: Array<ExperimentMethod>;
  procedureFlowchart: ExperimentFlowchart;
  selectedMethodId: string;
  onChangeSelectedMethod: (newMethodId: string) => void;
  procedureType: ProcedureType;
  context: ExperimentalInputsContextProperties;
}): JSX.Element {
  return (
    <div className={css(styles.experimentPlan)}>
      <ExperimentalInputsContext.Provider value={props.context}>
        <ProcedureRunPlan
          methods={props.methods}
          procedureFlowchart={props.procedureFlowchart}
          selectedMethodId={props.selectedMethodId}
          onChangeSelectedMethod={props.onChangeSelectedMethod}
          procedureType={props.procedureType}
        />
      </ExperimentalInputsContext.Provider>
    </div>
  );
}

function SettingsTabContents(props: {
  conditions: Array<Condition>;
  setConditions: (conditions: Array<Condition>, prevIndexes: Array<number | null>) => void;
  selectedMethodId: string;
  experimentalInputs: ExperimentalInputs | null;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  locked: boolean;
  procedureType: ProcedureType;
  methodExecutionLimitState: MethodExecutionLimitState;
  dataEntryMode: ProcedureRunDataEntryMode;
  studyType: StudyType;
  estimateResult: TimingEstimateResult;
}): JSX.Element {
  const editNumColsButtons = (
    <EditNumConditionsButtons
      conditions={props.conditions}
      setConditions={props.setConditions}
      methodExecutionLimitState={props.methodExecutionLimitState}
      procedureType={props.procedureType}
    />
  );
  const multiBatchButtonConfig = MULTI_BATCH_BUTTON_CONFIG_BY_DATA_ENTRY_MODE[props.dataEntryMode];

  return (
    <div className={css(styles.settingsTabContainer)}>
      <ReplicateTable
        conditions={props.conditions}
        setConditions={props.setConditions}
        selectedMethodId={props.selectedMethodId}
        experimentalInputs={props.experimentalInputs}
        setExperimentalInputs={props.setExperimentalInputs}
        editNumColsButtons={editNumColsButtons}
        locked={props.locked}
        estimate={props.estimateResult}
        procedureType={props.procedureType}
        dataTestAttrs={{component: 'ReplicateTable', element: 'replicateTable'}}
      />
      {props.methodExecutionLimitState.isOverLimit && (
        <div className={css(styles.methodExecutionLimitError)}>
          <MethodExecutionLimitError
            replicateLimit={props.methodExecutionLimitState.replicateLimit}
            procedureType={props.procedureType}
          />
        </div>
      )}
      {getTypedStudyAdapter(props.studyType).isMultiBatchEnabled && (
        <div className={css(styles.multiBatchButtonContainer)}>
          <div className={css(styles.dataEntryModeLabel)}>Data entry mode</div>
          <div className={css(styles.dataEntryModeDescription)}>
            Based on your earlier selection, all worksheets will be created with the following data entry
            mode.
          </div>
          <div className={css(styles.multiBatchButton)}>
            <MultiBatchButton
              graphic={multiBatchButtonConfig.graphic}
              headerText={multiBatchButtonConfig.headerText}
              description={multiBatchButtonConfig.description}
              dataTestAttrs={multiBatchButtonConfig.dataTestAttrs}
              onSelect={noop}
              // We don't allow the user to change the data entry mode
              isDisabled={true}
              active={false}
              includeRadioButton={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export interface ContextPanelProps {
  sidepanelName: string;
  sidepanelHeaderContent?: JSX.Element;
  /** Content to render inside the sidepanel */
  children: ReactNode;
}

function ContextPanel({
  children,
  sidepanelName,
  sidepanelHeaderContent,
}: ContextPanelProps): JSX.Element | null {
  return (
    <div className={css(styles.sidepanel)}>
      <div className={css(styles.sidepanelHeader)}>
        <h2 className={css(styles.sidepanelName)}>{sidepanelName}</h2>
        {sidepanelHeaderContent}
      </div>
      <div className={css(styles.sidepanelContent)}>{children}</div>
    </div>
  );
}

function MultiProcedureRunStudyDesignTabContents(props: {
  procedureRun: ProcedureRunNode & {
    templateProcedureFlowchartVersion: AccessibleTemplateProcedureFlowchartVersionNode;
    procedureFlowchartConfigVersionProxy: AccessibleProcedureFlowchartConfigVersionProxyNode | null;
  };
  studyType: StudyType;
  studyId: string;
  setCurrentProcedureRunTab: (tab: ProcedureRunTab) => void;
}): JSX.Element {
  const {procedureRun} = props;
  const {experimentalInputs: initialExperimentalInputs, templateFlowchart} = useMemo(
    () => getStudyDesignFromProcedureRun(procedureRun),
    [procedureRun]
  );
  const [selectedMethodId, setSelectedMethodId] = useState(templateFlowchart.nodes[0]?.id ?? null);
  // Use useStateIfMounted because we pass setEditState into the async callback for getConditionCreationUnknownErrorAction
  const [editState, setEditState] = useStateIfMounted(StudyDesignTabEditState.SAVED);
  const [selectedDesignTabIndex, setSelectedDesignTabIndex] = useState<number | null>(0);
  const [sidepanelName, setSidepanelName] = useState<string>(PARAMETER_VALUES_TAB_NAME);

  const initialConditions = useMemo(
    () =>
      procedureRun.conditions.map((condition) => ({
        id: condition.id,
        numReplicates: condition.procedureRunConditionReplicates.length,
        name: condition.name,
        description: condition.description ?? undefined,
      })),
    [procedureRun]
  );
  const flowchartVersion =
    procedureRun.procedureFlowchartConfigVersionProxy ?? procedureRun.templateProcedureFlowchartVersion;

  const procedureParent =
    procedureRun.procedureFlowchartConfigVersionProxy != null
      ? procedureRun.procedureFlowchartConfigVersionProxy.procedure
      : procedureRun.templateProcedureFlowchartVersion.procedureFlowchart.parent;

  assert(procedureParent != null, 'Flowchart version should always have a non-null parent.');
  const procedureId = procedureParent.id;

  const {conditions, setConditions, experimentalInputs, setExperimentalInputs} =
    useConditionsAndExperimentalInputs(
      {
        id: procedureId,
        procedureType: procedureRun.procedureType,
        methods: templateFlowchart.nodes,
      },
      procedureId,
      initialConditions,
      initialExperimentalInputs
    );
  // useConditionsAndExperimentalInputs returns null conditions when the procedure ID is null, which should
  // never be the case here
  assert(
    conditions != null && experimentalInputs != null,
    'Expected conditions and experimentalInputs to be non-null'
  );

  const context = useExperimentalInputsContextProperties(
    conditions,
    experimentalInputs,
    setExperimentalInputs,
    flowchartVersion,
    procedureRun.procedureType,
    // Should be locked when not in editing state
    editState !== StudyDesignTabEditState.EDITING
  );
  // Ideally `context` would have a conditional return type based on experimentalInputs null-ness.
  assert(context != null, 'Expected context to be non-null');

  const methodExecutionLimitState = useMethodExecutionLimitState(
    experimentalInputs,
    conditions,
    flowchartVersion.largestTableTypeSizes,
    procedureRun.defaultDataEntryMode
  );
  const procedureRunUpdateParam = useProcedureRunUpdateParam(
    procedureRun.id,
    conditions,
    experimentalInputs,
    context?.evaluatedValues
  );
  const estimateResult = useTimingEstimate<{updates: Array<UpdateProcedureRunParams>}>(
    '/1/api/procedure-runs:estimate',
    // Don't want an estimate if there are no new conditions
    procedureRunUpdateParam.newConditions && procedureRunUpdateParam.newConditions.length > 0
      ? {updates: [procedureRunUpdateParam]}
      : null
  );

  const editNumColsButtons = (
    <EditNumConditionsButtons
      conditions={conditions}
      setConditions={setConditions}
      methodExecutionLimitState={methodExecutionLimitState}
      procedureType={procedureRun.procedureType}
    />
  );

  async function handleSave(): Promise<void> {
    setEditState(StudyDesignTabEditState.SAVING);
    assert(conditions != null, 'Conditions should not be null for existing run');
    assert(experimentalInputs != null, 'Experimental inputs should not be null for existing run.');
    assert(context != null, 'Expected context to be non-null');

    await onAddConditions(
      props.studyId,
      procedureRun.procedureType,
      props.studyType,
      procedureRunUpdateParam,
      {
        onSuccess: async () => {
          await onSuccessAfterAddConditions(procedureRun.id);
        },
        onOpen: () => {
          onOpenAfterAddConditions(props.studyId, procedureRun.id);
          props.setCurrentProcedureRunTab(ProcedureRunTab.OVERVIEW);
        },
        onUnknownError: getConditionCreationUnknownErrorAction(
          props.studyId,
          props.studyType,
          procedureRun.id,
          procedureRun.procedureType,
          procedureRunUpdateParam.newConditions?.length ?? 0,
          // Go back to editing state, so the user can try to re-submit
          () => {
            setEditState(StudyDesignTabEditState.EDITING);
          }
        ),
      }
    );
  }

  return selectedMethodId == null ? (
    // Unexpected since we should prevent creating a procedure run if there are no methods, but handle it gracefully.
    <div className={css(styles.bufferTabWrapper)}>
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={`No ${getProcedureUIString(ProceduresStringKeys.METHOD, procedureRun.procedureType, {
          stringCase: StringCase.LOWER,
          plural: true,
        })} have been configured for this ${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          procedureRun.procedureType,
          {stringCase: StringCase.LOWER}
        )}.`}
      />
    </div>
  ) : (
    <div
      className={css(styles.designBackground)}
      {...getTestProps({component: 'MultiProcedureRunStudyDesignTabContents', element: 'root'})}
    >
      {!procedureRun.flowchartIsExpanded && (
        <div className={css(styles.header)}>
          <div className={css(styles.tabTitle)}>Design</div>
          <div className={css(styles.bannerAndButtons)}>
            {editState === StudyDesignTabEditState.EDITING && (
              <InlineBanner
                bannerType={InlineBannerType.INFO}
                text="New worksheets will be created for new conditions."
              />
            )}
            <StudyDesignTabSavingButtons
              onCancel={() => {
                setEditState(StudyDesignTabEditState.SAVED);
                setConditions(initialConditions, getInitialConditionIndices(initialConditions, conditions));
              }}
              onEdit={() => {
                setEditState(StudyDesignTabEditState.EDITING);
              }}
              onSave={handleSave}
              editState={editState}
              isSaveDisabled={
                !areProcedureRunInputsEvaluatedValuesValid(context.evaluatedValues) ||
                !validateConditions(conditions) ||
                !isNumConditionReplicatesValid(methodExecutionLimitState) ||
                conditions.length === initialConditions.length
              }
            />
          </div>
        </div>
      )}
      <div className={css(styles.leftSidepanelContainer)}>
        {/* We eventually want a flowchart view, which is why we're using the FlowchartSidebar component. */}
        <FlowchartSidebar
          icons={[
            {
              name: 'note-table',
              onClick: () => {
                setSidepanelName(PARAMETER_VALUES_TAB_NAME);
              },
            },
            {
              name: 'settings',
              onClick: () => {
                setSidepanelName(SETTINGS_TAB_NAME);
              },
            },
          ]}
          selectedIconIndex={selectedDesignTabIndex}
          setSelectedIconIndex={setSelectedDesignTabIndex}
        />
        {/* TODO: Use FlowchartContextPanel here when we actually have a flowchart */}
        <ContextPanel
          sidepanelName={sidepanelName}
          sidepanelHeaderContent={
            selectedDesignTabIndex === 0 && editState === StudyDesignTabEditState.EDITING
              ? editNumColsButtons
              : undefined
          }
        >
          {selectedDesignTabIndex === 0 ? (
            <ParameterValuesTabContents
              methods={templateFlowchart.nodes}
              procedureFlowchart={templateFlowchart}
              selectedMethodId={selectedMethodId}
              onChangeSelectedMethod={setSelectedMethodId}
              procedureType={procedureRun.procedureType}
              context={context}
            />
          ) : (
            <SettingsTabContents
              conditions={conditions}
              setConditions={setConditions}
              selectedMethodId={selectedMethodId}
              experimentalInputs={experimentalInputs}
              setExperimentalInputs={setExperimentalInputs}
              locked={editState !== StudyDesignTabEditState.EDITING}
              procedureType={procedureRun.procedureType}
              methodExecutionLimitState={methodExecutionLimitState}
              studyType={props.studyType}
              dataEntryMode={procedureRun.defaultDataEntryMode}
              estimateResult={estimateResult}
            />
          )}
        </ContextPanel>
      </div>
    </div>
  );
}

export default function QueryingMultiProcedureRunStudyDesignTabContents({
  procedureRunId,
  studyType,
  studyId,
  setCurrentProcedureRunTab,
}: {
  procedureRunId: string;
  studyType: StudyType;
  studyId: string;
  setCurrentProcedureRunTab: (tab: ProcedureRunTab) => void;
}): JSX.Element {
  const result = useBenchlingQuery<
    MultiProcedureRunStudyDesignTabContentsQuery,
    MultiProcedureRunStudyDesignTabContentsQueryVariables
  >(MULTI_PROCEDURE_RUN_STUDY_DESIGN_TAB_CONTENTS_QUERY, {variables: {procedureRunId}});
  if (result.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  } else {
    const {data} = result;
    if (isInaccessible(data.procedureRun)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.procedureRun}
          modelType={ModelTypeConstants.PROCEDURE_RUN}
          displayErrorText={`This ${getProcedureRunUIStringForStudyType(studyType)} is no longer accessible. The ${getProcedureRunUIStringForStudyType(studyType)} may have been moved into a project that you do not have access to.`}
        />
      );
    } else if (
      data.procedureRun.procedureFlowchartConfigVersionProxy != null &&
      isInaccessible(data.procedureRun.procedureFlowchartConfigVersionProxy)
    ) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.procedureRun.procedureFlowchartConfigVersionProxy}
          modelType={ModelTypeConstants.PROCEDURE}
          displayErrorText={`The design of this ${getProcedureRunUIStringForStudyType(studyType)} is not accessible. The ${getProcedureTemplateStringForStudyType(studyType)} associated with this ${getProcedureRunUIStringForStudyType(studyType)} may have been moved into a template collection that you do not have access to.`}
        />
      );
    } else if (isInaccessible(data.procedureRun.templateProcedureFlowchartVersion)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.procedureRun.templateProcedureFlowchartVersion}
          modelType={ModelTypeConstants.PROCEDURE}
          displayErrorText={`The design of this ${getProcedureRunUIStringForStudyType(studyType)} is not accessible. The ${getProcedureTemplateStringForStudyType(studyType)} associated with this ${getProcedureRunUIStringForStudyType(studyType)} may have been moved into a template collection that you do not have access to.`}
        />
      );
    } else {
      return (
        <MultiProcedureRunStudyDesignTabContents
          procedureRun={{
            ...data.procedureRun,
            templateProcedureFlowchartVersion: data.procedureRun.templateProcedureFlowchartVersion,
            procedureFlowchartConfigVersionProxy: data.procedureRun.procedureFlowchartConfigVersionProxy,
          }}
          studyType={studyType}
          studyId={studyId}
          setCurrentProcedureRunTab={setCurrentProcedureRunTab}
        />
      );
    }
  }
}

const styles = StyleSheet.create({
  bannerAndButtons: {display: 'flex', alignItems: 'center'},
  bufferTabWrapper: {paddingTop: 10, paddingBottom: 10},
  dataEntryModeLabel: {...FONT_PROXIMA_TITLE, color: NEAR_BLACK, marginBottom: 8},
  dataEntryModeDescription: {color: NEAR_BLACK, marginBottom: 12},
  designBackground: {background: Color.WHITE},
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 33px 8px 12px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  settingsTabContainer: {display: 'flex', flexDirection: 'column', flex: 1},
  experimentPlan: {minHeight: 0, flex: 1},
  leftSidepanelContainer: {display: 'flex', position: 'absolute', height: '100%', width: '100%'},
  methodExecutionLimitError: {display: 'flex', marginLeft: '10px'},
  multiBatchButton: {backgroundColor: Color.GRAY_50},
  multiBatchButtonContainer: {marginTop: '20px', marginLeft: '10px', width: '555px'},
  // sidepanel styling is aligned with FlowchartContextPanel
  sidepanel: {backgroundColor: Color.WHITE, flex: 1, display: 'flex', flexDirection: 'column'},
  sidepanelContent: {display: 'flex', flex: 1},
  sidepanelHeader: {
    padding: '12px 12px',
    borderBottom: `1px solid ${Color.GRAY_200}`,
    display: 'flex',
    alignItems: 'center',
  },
  sidepanelName: {margin: 0, fontSize: '13px', fontWeight: 600},
  tabTitle: {
    color: NEAR_BLACK,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '16px',
    display: 'flex',
    alignItems: 'center',
  },
});
